import React from "react";
import { useTranslation } from "react-i18next";
import {
    Divider,
    Text,
    useColorModeValue,
    Box
} from "@chakra-ui/react";
import { LOCALE_PT_BR } from "constants/date-time";
import TimelineRow from "components/Tables/TimelineRow";
import { FaUserEdit, FaUserPlus, FaUserShield } from "react-icons/fa";
import { HiOutlineUserGroup } from "react-icons/hi";
import { MdSecurity, MdSwitchAccount } from "react-icons/md";
import { RiLockPasswordLine } from "react-icons/ri";
import { BsPersonBadge } from "react-icons/bs";
import { isDifferentValue, toTitleCase } from "utils/utils";

function UserTimeline({ creationDate, historyData }) {
    const { t } = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");

    const iconMap = {
        codigo_agente: BsPersonBadge,
        funcao: FaUserShield,
        login: MdSwitchAccount,
        nome_pessoa: FaUserEdit,
        nome: FaUserEdit,
        perfis: HiOutlineUserGroup,
        permissoes: MdSecurity,
        senha_modificada: RiLockPasswordLine,
    };

    const getChangeMessage = (key, oldValue, newValue) => {
        const basePath = "settings.user.details.tab.timeline";

        if (key === "senha_modificada") {
            return t(`${basePath}.changes.password-changed`);
        }

        if (key === "cpf_cnpj") {
            return t(`${basePath}.changes.cpf-cnpj-changed`, { oldValue, newValue });
        }

        if (key === "funcao") {
            return t(`${basePath}.changes.role-changed`, { oldValue, newValue });
        }

        if (key === "perfis" || key === "permissoes") {
            const added = newValue.filter(item => !oldValue.includes(item));
            const removed = oldValue.filter(item => !newValue.includes(item));
            const messages = [];

            if (added.length) {
                messages.push(
                    t(`${basePath}.changes.${key === "perfis" ? "profiles-added" : "permissions-added"}`, {
                        items: added.join(", ")
                    })
                );
            }

            if (removed.length) {
                messages.push(
                    t(`${basePath}.changes.${key === "perfis" ? "profiles-removed" : "permissions-removed"}`, {
                        items: removed.join(", ")
                    })
                );
            }

            return messages;
        }

        return t(`${basePath}.changes.generic-change`, {
            key: toTitleCase(key),
            oldValue,
            newValue
        });
    };

    const timelineData = [
        ...(creationDate
            ? [{
                key: "creation",
                title: t("settings.user.details.tab.timeline.changes.data-creation"),
                date: creationDate,
                logo: FaUserPlus
            }]
            : []),

        ...historyData.map((entry, i) => {
            let icon = null;
            const messages = [];

            Object.entries(entry.dados_antigos).forEach(([key, oldValue]) => {
                const newValue = entry.dados_novos[key];

                if (isDifferentValue(oldValue, newValue)) {
                    if (!icon && iconMap[key]) {
                        icon = iconMap[key];
                    }

                    const msg = getChangeMessage(key, oldValue, newValue);
                    if (Array.isArray(msg)) {
                        messages.push(...msg);
                    } else {
                        messages.push(msg);
                    }
                }
            });

            return {
                key: `history-${i}`,
                title: messages.join(". "),
                date: new Date(entry.data).toLocaleString(LOCALE_PT_BR),
                logo: icon
            };
        })
    ];

    return (
        <>
            <Text fontSize="md" fontWeight="bold" color={textColor}>
                {t("settings.user.details.tab.timeline.title")}
            </Text>
            <Divider borderColor="#3884cc" />
            <Box m="4">
                {timelineData.map((entry, index) => (
                    <TimelineRow
                        key={entry.key}
                        title={entry.title}
                        titleColor={textColor}
                        date={entry.date}
                        color={textColor}
                        logo={entry.logo}
                        index={index}
                        arrLength={timelineData.length}
                    />
                ))}
            </Box>
        </>
    );
}

export default UserTimeline;
